"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import {
  Eye,
  EyeOff,
  FileText,
  LogOut,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";

import {
  deleteNoteAction,
  setNoteStatusAction,
} from "@/app/admin/notes/actions";
import { signOutAction } from "@/app/admin/auth-actions";
import type { AdminSession } from "@/lib/admin";

export type AdminBarNote = {
  id: number;
  slug: string;
  title: string;
  status: string;
};

const BUTTON =
  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] text-white/70 transition-colors duration-200 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60";

export default function AdminBarClient({
  admin,
  notes,
}: {
  admin: AdminSession;
  notes: AdminBarNote[];
}) {
  const pathname = usePathname() ?? "";
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const slugMatch = pathname.match(/^\/notes\/([^/]+)\/?$/);
  const current = slugMatch
    ? notes.find((note) => note.slug === slugMatch[1])
    : undefined;
  const isPublished = current?.status === "published";

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex justify-start px-4 pb-4 pointer-events-none">
      <div className="pointer-events-auto flex max-w-full flex-wrap items-center gap-1 rounded-full bg-[#111111] px-3 py-2 shadow-[0_8px_30px_rgba(0,0,0,0.25)]">
        <span className="flex items-center gap-2 pl-1 pr-2">
          {admin.image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={admin.image}
              alt=""
              className="h-6 w-6 rounded-full"
              width={24}
              height={24}
            />
          ) : null}
          <span className="font-pp-neuebit text-[18px] leading-none text-white/60">
            @{admin.login}
          </span>
        </span>

        {current ? (
          <span
            className={`rounded-full border px-2 py-0.5 text-[11px] ${
              isPublished
                ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                : "border-amber-400/30 bg-amber-400/10 text-amber-300"
            }`}
          >
            {isPublished ? "Published" : "Draft"}
          </span>
        ) : null}

        <span className="mx-1 h-5 w-px bg-white/10" aria-hidden="true" />

        {current ? (
          <>
            <Link href={`/admin/notes/${current.id}`} className={BUTTON}>
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Link>

            <form action={setNoteStatusAction}>
              <input type="hidden" name="id" value={current.id} />
              <input
                type="hidden"
                name="status"
                value={isPublished ? "draft" : "published"}
              />
              <button type="submit" className={BUTTON}>
                {isPublished ? (
                  <EyeOff className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
                {isPublished ? "Unpublish" : "Publish"}
              </button>
            </form>

            {confirmingDelete ? (
              <form action={deleteNoteAction} className="flex items-center">
                <input type="hidden" name="id" value={current.id} />
                <button
                  type="submit"
                  className={`${BUTTON} text-red-300 hover:bg-red-500/20 hover:text-red-200`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Really delete
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  className={BUTTON}
                >
                  Cancel
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                className={BUTTON}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            )}
          </>
        ) : (
          <Link href="/admin/notes/new" className={BUTTON}>
            <Plus className="h-3.5 w-3.5" />
            New note
          </Link>
        )}

        <Link href="/admin/notes" className={BUTTON}>
          <FileText className="h-3.5 w-3.5" />
          All notes
        </Link>

        <form action={signOutAction}>
          <button type="submit" className={BUTTON} aria-label="Sign out">
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </form>
      </div>
    </div>
  );
}
